/*
  Aura Farmers global JS. No dependencies.
  - cart count helper (window.Aura.setCartCount / refreshCart, or dispatch "cart:updated")
  - drop countdown ([data-countdown])
  - mobile menu close on Escape / outside click
  - sticky add to cart observer (stub, wired up by the product section later)
*/
(() => {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Cart count */

  const bump = (el) => {
    el.classList.remove('is-bumping');
    // Force a reflow so the animation restarts on rapid adds
    void el.offsetWidth;
    el.classList.add('is-bumping');
  };

  const setCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (el.textContent === String(count)) return;
      el.textContent = count;
      bump(el);
    });

    document.querySelectorAll('[data-cart-link][data-label]').forEach((link) => {
      link.setAttribute('aria-label', link.dataset.label.replace('%count%', count));
    });
  };

  const refreshCart = () =>
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        setCartCount(cart.item_count);
        return cart;
      })
      .catch(() => null);

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail;
    if (cart && Number.isInteger(cart.item_count)) setCartCount(cart.item_count);
    else refreshCart();
  });

  // Back/forward cache can restore a page with a stale count
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });

  window.Aura = Object.assign(window.Aura || {}, { setCartCount, refreshCart });

  /* Countdown */

  const pad = (n) => String(n).padStart(2, '0');

  const initCountdown = (el) => {
    if (el.dataset.countdownReady) return;

    const output = el.querySelector('[data-countdown-time]');
    const target = Date.parse(el.dataset.countdown);
    if (!output || Number.isNaN(target)) return;

    el.dataset.countdownReady = 'true';
    const suffix = el.dataset.daysSuffix || 'd';
    const endedText = el.dataset.endedText || '';
    let timer;

    const tick = () => {
      if (!el.isConnected) {
        clearInterval(timer);
        return;
      }

      const left = Math.max(0, Math.floor((target - Date.now()) / 1000));

      if (left === 0) {
        clearInterval(timer);
        el.classList.add('is-ended');
        output.textContent = endedText;
        el.hidden = !endedText;
        return;
      }

      const days = Math.floor(left / 86400);
      const hours = Math.floor((left % 86400) / 3600);
      const minutes = Math.floor((left % 3600) / 60);
      const seconds = left % 60;
      output.textContent = `${days ? `${days}${suffix} ` : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    };

    tick();
    el.hidden = false;
    timer = setInterval(tick, 1000);
  };

  /* Mobile menu (details/summary) */

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('details[data-menu][open]');
    if (!menu) return;
    menu.removeAttribute('open');
    const summary = menu.querySelector('summary');
    if (summary) summary.focus();
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.removeAttribute('open');
    });
  });

  /* Sticky add to cart (stub)
     The product section will provide:
       [data-sticky-atc-trigger]  the main add to cart button
       [data-sticky-atc]          the sticky bar, hidden and inert until the trigger scrolls out above the viewport
     The bar's styles key off .is-visible. */

  let stickyObserver;

  const initStickyAtc = () => {
    if (stickyObserver) stickyObserver.disconnect();

    const trigger = document.querySelector('[data-sticky-atc-trigger]');
    const bar = document.querySelector('[data-sticky-atc]');
    if (!trigger || !bar || !('IntersectionObserver' in window)) return;

    const setVisible = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.toggleAttribute('inert', !visible);
    };

    setVisible(false);
    stickyObserver = new IntersectionObserver(([entry]) => {
      setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    stickyObserver.observe(trigger);
  };

  /* Init, and again when the theme editor reloads a section */

  const init = () => {
    document.querySelectorAll('[data-countdown]').forEach(initCountdown);
    initStickyAtc();
  };

  init();
  document.addEventListener('shopify:section:load', init);
})();

// Category chips: keep the active chip in view when the bar scrolls sideways.
(() => {
  const centerActiveChip = () => {
    document.querySelectorAll('.chips__list').forEach((list) => {
      const active = list.querySelector('[aria-current="page"]');
      if (!active || list.scrollWidth <= list.clientWidth) return;
      const item = active.closest('li') || active;
      // Leave the bar at its start when the active chip is one of the first three,
      // so the leading chips (Halloween, Thanksgiving) stay visible on mobile.
      if (Array.prototype.indexOf.call(list.children, item) < 3) return;
      const offset = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
      list.scrollLeft = Math.max(0, offset);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', centerActiveChip);
  else centerActiveChip();
})();
